import type { StatusVariant } from '@/components/ui/status-badge';

/**
 * The AI allowance contract and its state machine (Plan Phase 6-shared).
 *
 * Producer: `GET /v1/me/ai-allowance`. This module owns three things and
 * nothing else:
 *   1. the FROZEN wire types,
 *   2. `resolveCreditState()` — the single reconciler that turns a wire
 *      allowance into the one state literal every AI surface renders from,
 *   3. `CREDIT_STATE_META` — the styling/label table, modelled directly on
 *      `components/admin/observability/status-meta.ts`.
 *
 * ── The non-negotiable ──────────────────────────────────────────────────────
 * `unknown` is a FIRST-CLASS state and must never render as healthy. This is
 * the same rule `components/admin/observability/history-helpers.ts` locked in
 * as `up === total` after the 2026-07-26 nine-false-greens review: absence of
 * bad news is not good news. A credit probe that could not answer is not a
 * tenant with credits to spare, and painting it green is how an operator
 * discovers the ceiling by hitting it mid-campaign.
 */

// ── Frozen wire contract ────────────────────────────────────────────────────

/** The five literals the producer can emit. */
export type AiCreditState =
  | 'healthy'
  | 'warning'
  | 'exhausted'
  | 'degraded'
  | 'unknown';

export type AiRemedy = 'add_own_key' | 'ask_admin';

export type AiKeyScope = 'platform' | 'customer' | 'mixed' | 'unknown';

export interface AiAllowanceWindow {
  kind: 'calendar-month-utc';
  startsAt: string;
  endsAt: string;
  resetsAt: string;
}

export interface AiModelTier {
  requested: 'pro' | 'mini';
  /** Null until something has actually been served this window. */
  served: 'pro' | 'mini' | null;
  locked: boolean;
}

export interface AiAllowance {
  state: AiCreditState;
  /**
   * WHICH ceiling blocked, when one did. Two exist and they reset on
   * different clocks — monthly credits at the start of next month, the daily
   * output-token ceiling at UTC midnight. Rendering one string for both told
   * a customer at 3% of their monthly credits to come back next month when
   * the block actually lifted in hours (observed on staging, 2026-08-06).
   */
  blockedBy: 'credits' | 'daily-tokens' | null;
  /** False during observe mode — the ceiling is measured but not applied. */
  enforcing: boolean;
  /** Null = withheld (MSSP) or genuinely unknown. NEVER coalesce this to 0. */
  creditsRemaining: number | null;
  creditsIncluded: number | null;
  percentUsed: number | null;
  /** False => show the state, withhold the figure. */
  numberVisible: boolean;
  window: AiAllowanceWindow;
  warnAtPct: number;
  keyScope: AiKeyScope;
  /**
   * Ability-filtered SERVER-side. `create:AiStudio` is a workspace EDITOR
   * grant; `manage:AiStudio` (needed to add a key) is ADMIN-only, so an editor
   * receives `['ask_admin']` and never `['add_own_key']`.
   *
   * Consumers render exactly what arrives here. They must NOT re-derive it
   * from a local ability check — two sources of truth on a permission is how
   * you end up deep-linking an editor into a dialog they cannot open.
   */
  remedies: AiRemedy[];
  modelTier: AiModelTier;
}

const CREDIT_STATES: readonly AiCreditState[] = [
  'healthy',
  'warning',
  'exhausted',
  'degraded',
  'unknown',
];

const REMEDIES: readonly AiRemedy[] = ['add_own_key', 'ask_admin'];

const KEY_SCOPES: readonly AiKeyScope[] = [
  'platform',
  'customer',
  'mixed',
  'unknown',
];

/** Default warn threshold when the producer sends none. */
export const DEFAULT_WARN_AT_PCT = 80;

// ── Resolution ──────────────────────────────────────────────────────────────

/**
 * Severity ladder. `resolveCreditState` only ever ESCALATES along it, never
 * de-escalates, so no single input can talk the UI down into a cheerier state
 * than the worst thing we know.
 *
 * `unknown` deliberately outranks `healthy`: given a server that says "I could
 * not tell" and a percent that says "10% used", the honest answer is "we do
 * not know", not green.
 *
 * `warning` outranks `degraded` only on THIS ladder, and that is safe because
 * the degradation notice is NOT driven from here — `isModelDegraded()` reads
 * `modelTier` directly. A run that is both degraded and at 85% therefore
 * resolves to `warning` AND still renders `<AiDegradedNotice>`. Folding the
 * two into one literal is exactly how a silent PRO→MINI drop ships unnoticed.
 */
const SEVERITY: Record<AiCreditState, number> = {
  healthy: 0,
  unknown: 1,
  degraded: 2,
  warning: 3,
  exhausted: 4,
};

export const CREDIT_STATE_ORDER: readonly AiCreditState[] = [
  'healthy',
  'unknown',
  'degraded',
  'warning',
  'exhausted',
];

function isCreditState(value: unknown): value is AiCreditState {
  return (
    typeof value === 'string' && CREDIT_STATES.includes(value as AiCreditState)
  );
}

/**
 * Derive a state from the percentage alone.
 *
 * The threshold comparison is `>=`, so a tenant sitting on EXACTLY `warnAtPct`
 * is already warned. `>` would leave the boundary case — the most common one,
 * because thresholds are round numbers — with no banner at all.
 *
 * A non-finite or absent percent yields `unknown`, never `healthy`: `NaN >= 80`
 * is false, and letting that fall through to the healthy branch would turn a
 * parse failure into a green light.
 */
function deriveFromPercent(
  percentUsed: unknown,
  warnAtPct: number,
): AiCreditState {
  if (typeof percentUsed !== 'number' || !Number.isFinite(percentUsed)) {
    return 'unknown';
  }
  if (percentUsed >= 100) return 'exhausted';
  if (percentUsed >= warnAtPct) return 'warning';
  return 'healthy';
}

export interface ResolvableAllowance {
  state?: AiCreditState | string | null;
  percentUsed?: number | null;
  warnAtPct?: number | null;
}

/**
 * The one place a credit state is decided. Every AI surface calls this rather
 * than reading `allowance.state` directly, so the escalation rules below hold
 * everywhere at once.
 *
 * Precedence:
 *   - An absent/null `state` means "the producer did not say" → derive purely
 *     from the percent.
 *   - A recognised `state` is reconciled with the derived one by taking the
 *     MORE SEVERE of the two. So a producer still reporting `healthy` while the
 *     percent reads 100 resolves to `exhausted`, and a producer reporting
 *     `exhausted` is not softened by a stale low percent.
 *   - An unrecognised literal (a backend code this frontend has not learned)
 *     is treated as `unknown` — neutral, never green.
 */
export function resolveCreditState(allowance: ResolvableAllowance): AiCreditState {
  const warnAtPct =
    typeof allowance.warnAtPct === 'number' &&
    Number.isFinite(allowance.warnAtPct)
      ? allowance.warnAtPct
      : DEFAULT_WARN_AT_PCT;

  const derived = deriveFromPercent(allowance.percentUsed, warnAtPct);

  // `undefined`/`null` = "not reported". Anything else, including a literal we
  // do not recognise, is reported — and an unrecognised one reports `unknown`.
  const reported: AiCreditState | null =
    allowance.state === undefined || allowance.state === null
      ? null
      : isCreditState(allowance.state)
        ? allowance.state
        : 'unknown';

  if (reported === null) return derived;
  return SEVERITY[reported] >= SEVERITY[derived] ? reported : derived;
}

/**
 * Did the run actually come back at a lower tier than was asked for?
 *
 * Read straight off `modelTier`, deliberately NOT off the resolved credit
 * state — see the SEVERITY comment. `served: null` means nothing has been
 * served yet this window, which is not a downgrade and must not raise a
 * notice.
 */
export function isModelDegraded(modelTier: AiModelTier | null | undefined): boolean {
  if (!modelTier) return false;
  if (modelTier.served === null) return false;
  return modelTier.requested === 'pro' && modelTier.served === 'mini';
}

// ── Styling / labelling ─────────────────────────────────────────────────────

export interface CreditStateMeta {
  /** Solid swatch class — dots and progress fills. */
  swatch: string;
  /**
   * The same colour as `swatch`, addressed at `<Progress>`'s inner indicator.
   *
   * `components/ui/progress.tsx` has no variant API — its indicator is hard-
   * coded `bg-primary` — so the only way to tint a bar is the arbitrary-child
   * variant. Precedent: `components/er/threat-logs/threat-logs-columns.tsx`
   * (`scoreTint`), which does exactly this for risk scores.
   *
   * It lives in this table rather than in each card because the alternative is
   * every usage surface hand-rolling its own threshold ladder — and then
   * disagreeing about where amber starts.
   */
  progressTint: string;
  /** Background + text + border class for a pill badge. */
  pill: string;
  /** Human label. Every state renders this text alongside colour — never colour alone. */
  label: string;
  /** Variant for the shared `<StatusBadge>`. */
  badge: StatusVariant;
  /** Variant for the shared `<Alert>` (see components/ui/alert.tsx). */
  alertVariant: 'default' | 'warning' | 'info' | 'destructive';
}

/**
 * Mirrors `STATUS_META` in the observability console, including its most
 * important property: `unknown` is a solid NEUTRAL fill and is never
 * confusable with the healthy style. If you add a state, style it here — no
 * surface may hand-roll its own colour map.
 */
export const CREDIT_STATE_META: Record<AiCreditState, CreditStateMeta> = {
  healthy: {
    swatch: 'bg-emerald-600 dark:bg-emerald-400',
    progressTint: '[&>div]:bg-emerald-600 dark:[&>div]:bg-emerald-400',
    pill: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    label: 'Included credits available',
    badge: 'success',
    alertVariant: 'default',
  },
  warning: {
    swatch: 'bg-amber-600 dark:bg-amber-400',
    progressTint: '[&>div]:bg-amber-500',
    pill: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    label: 'Running low',
    badge: 'warning',
    alertVariant: 'warning',
  },
  exhausted: {
    swatch: 'bg-red-600 dark:bg-red-400',
    progressTint: '[&>div]:bg-destructive',
    pill: 'bg-destructive/10 text-destructive border-destructive/25',
    label: 'Used up',
    badge: 'destructive',
    alertVariant: 'destructive',
  },
  degraded: {
    swatch: 'bg-blue-600 dark:bg-blue-400',
    progressTint: '[&>div]:bg-blue-600 dark:[&>div]:bg-blue-400',
    pill: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/25',
    label: 'Reduced quality',
    badge: 'info',
    alertVariant: 'info',
  },
  unknown: {
    // Neutral, never green — the whole point of the state existing.
    swatch: 'bg-muted-foreground/40',
    progressTint: '[&>div]:bg-muted-foreground/40',
    pill: 'bg-muted text-muted-foreground border-border',
    label: 'Not known',
    badge: 'muted',
    alertVariant: 'default',
  },
};

/** Falls back to the neutral entry for an unrecognised literal rather than throwing. */
export function creditStateMeta(state: string): CreditStateMeta {
  return CREDIT_STATE_META[state as AiCreditState] ?? CREDIT_STATE_META.unknown;
}

// ── Normalization (the hook layer's job, per apps/web/CLAUDE.md rule 1) ──────

/**
 * What every consumer gets when we could not obtain a real answer.
 *
 * Deliberately: `state: 'unknown'`, no figures, and NO remedies. Offering a
 * remedy we could not confirm the caller is permitted to perform would violate
 * the same rule the server-side filtering exists for.
 */
export const UNKNOWN_ALLOWANCE: AiAllowance = {
  state: 'unknown',
  enforcing: false,
  blockedBy: null,
  creditsRemaining: null,
  creditsIncluded: null,
  percentUsed: null,
  numberVisible: false,
  window: {
    kind: 'calendar-month-utc',
    startsAt: '',
    endsAt: '',
    resetsAt: '',
  },
  warnAtPct: DEFAULT_WARN_AT_PCT,
  keyScope: 'unknown',
  remedies: [],
  modelTier: { requested: 'pro', served: null, locked: false },
};

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeWindow(value: unknown): AiAllowanceWindow {
  if (!value || typeof value !== 'object') return UNKNOWN_ALLOWANCE.window;
  const rec = value as Record<string, unknown>;
  const str = (k: string) => (typeof rec[k] === 'string' ? (rec[k] as string) : '');
  return {
    kind: 'calendar-month-utc',
    startsAt: str('startsAt'),
    endsAt: str('endsAt'),
    resetsAt: str('resetsAt'),
  };
}

function normalizeModelTier(value: unknown): AiModelTier {
  if (!value || typeof value !== 'object') return UNKNOWN_ALLOWANCE.modelTier;
  const rec = value as Record<string, unknown>;
  const tier = (k: string): 'pro' | 'mini' | null =>
    rec[k] === 'pro' || rec[k] === 'mini' ? (rec[k] as 'pro' | 'mini') : null;
  return {
    requested: tier('requested') ?? 'pro',
    served: tier('served'),
    locked: rec.locked === true,
  };
}

/**
 * Turn whatever came off the wire into a guaranteed non-null `AiAllowance`.
 *
 * This is the ONLY place nullish handling for the allowance lives — pages and
 * components are forbidden from writing `?? []` (apps/web/CLAUDE.md rule 1),
 * so they receive real arrays and real objects from here or nothing at all.
 *
 * Anything unrecognised — including the dropped `'upgrade'` remedy an older
 * backend might still send — is discarded rather than forwarded. The billing
 * page's Contact Sales button is inert, so an upgrade CTA would be a dead end.
 */
export function normalizeAiAllowance(raw: unknown): AiAllowance {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return UNKNOWN_ALLOWANCE;
  }

  const rec = raw as Record<string, unknown>;

  const remedies = Array.isArray(rec.remedies)
    ? rec.remedies.filter((r): r is AiRemedy => REMEDIES.includes(r as AiRemedy))
    : [];

  return {
    state: isCreditState(rec.state) ? rec.state : 'unknown',
    // Only an explicit `true` enforces. An absent flag is not permission to
    // block someone.
    enforcing: rec.enforcing === true,
    // Only the two the server can name. Anything else is treated as "we do
    // not know which ceiling", which renders the neutral copy rather than
    // guessing "this month" at someone blocked until midnight.
    blockedBy:
      rec.blockedBy === 'credits' || rec.blockedBy === 'daily-tokens'
        ? rec.blockedBy
        : null,
    creditsRemaining: numberOrNull(rec.creditsRemaining),
    creditsIncluded: numberOrNull(rec.creditsIncluded),
    percentUsed: numberOrNull(rec.percentUsed),
    // Withhold when unsure: only an explicit `true` reveals a figure.
    numberVisible: rec.numberVisible === true,
    window: normalizeWindow(rec.window),
    warnAtPct: numberOrNull(rec.warnAtPct) ?? DEFAULT_WARN_AT_PCT,
    keyScope: KEY_SCOPES.includes(rec.keyScope as AiKeyScope)
      ? (rec.keyScope as AiKeyScope)
      : 'unknown',
    remedies,
    modelTier: normalizeModelTier(rec.modelTier),
  };
}
